import { zodResolver } from "@hookform/resolvers/zod";
import { ScrollText, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type SoapApiLogSettings,
  type SoapApiLogSettingsInput,
  type SoapApiLogStats,
  usePurgeSoapApiCallLog,
  useSoapApiLogSettings,
  useUpdateSoapApiLogSettings,
} from "@/hooks/use-soap-api-log-settings";
import { updateSoapApiLogSettingsSchema } from "@/schemas/soap-api-log-settings";

// Prefilled when "keep forever" is unchecked, so the field has a sane value
// to edit from rather than popping in empty.
const DEFAULT_ERROR_RETENTION_DAYS = 30;

export function SettingsApiLogsTab() {
  const { data, isLoading, isError, error, refetch } = useSoapApiLogSettings();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load API log settings"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading || !data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return <ApiLogSettingsForm key={data.settings.id} settings={data.settings} stats={data.stats} />;
}

function StatItem({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold">{value.toLocaleString()}</dd>
    </div>
  );
}

interface ApiLogSettingsFormProps {
  settings: SoapApiLogSettings;
  stats: SoapApiLogStats;
}

function ApiLogSettingsForm({ settings, stats }: ApiLogSettingsFormProps) {
  const updateSettings = useUpdateSoapApiLogSettings();
  const purge = usePurgeSoapApiCallLog();
  const [purgeOpen, setPurgeOpen] = useState(false);

  const form = useForm<SoapApiLogSettingsInput>({
    resolver: zodResolver(updateSoapApiLogSettingsSchema),
    defaultValues: {
      loggingEnabled: settings.loggingEnabled,
      successRetentionDays: settings.successRetentionDays,
      errorRetentionDays: settings.errorRetentionDays,
    },
  });

  const errorRetentionDays = form.watch("errorRetentionDays");
  const keepErrorsForever = errorRetentionDays === null;
  const totalPurgeableNow = stats.successPurgeableNow + stats.errorPurgeableNow;

  function onSubmit(values: SoapApiLogSettingsInput) {
    updateSettings.mutate(values);
  }

  return (
    <div className="space-y-6">
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ScrollText className="h-4 w-4 text-primary" />
            SOAP API call log
          </CardTitle>
          <CardDescription>
            Controls whether successful Coach Manager SOAP calls are recorded to the API Call Log,
            and how long rows are kept before they can be purged.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatItem label="Total rows" value={stats.totalRows} />
            <StatItem label="Success rows" value={stats.successRows} />
            <StatItem label="Error rows" value={stats.errorRows} />
            <StatItem label="Purgeable now" value={totalPurgeableNow} />
          </dl>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="loggingEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Capture successful API calls</FormLabel>
                      <FormDescription>
                        Error and failed calls are always captured, regardless of this setting.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="successRetentionDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Retention for successful calls (days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={3650}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="errorRetentionDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Retention for error/failed calls (days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={3650}
                          disabled={keepErrorsForever}
                          value={field.value ?? DEFAULT_ERROR_RETENTION_DAYS}
                          onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        />
                      </FormControl>
                      <div className="flex items-center gap-2 pt-1">
                        <Checkbox
                          id="keep-errors-forever"
                          checked={keepErrorsForever}
                          onCheckedChange={(checked) =>
                            field.onChange(checked === true ? null : DEFAULT_ERROR_RETENTION_DAYS)
                          }
                        />
                        <Label htmlFor="keep-errors-forever" className="font-normal">
                          Keep forever
                        </Label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button type="submit" disabled={updateSettings.isPending}>
                {updateSettings.isPending ? "Saving..." : "Save changes"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trash2 className="h-4 w-4 text-destructive" />
            Purge old logs
          </CardTitle>
          <CardDescription>
            Deletes rows past the retention windows above, using the currently saved settings — save
            changes first if you just edited them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="destructive"
            onClick={() => setPurgeOpen(true)}
            disabled={totalPurgeableNow === 0}
          >
            Purge now ({totalPurgeableNow} rows)
          </Button>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        title="Purge old API call log rows"
        description={`This will permanently delete ${stats.successPurgeableNow} success and ${stats.errorPurgeableNow} error row(s) older than the saved retention windows. This cannot be undone.`}
        confirmLabel="Purge"
        variant="destructive"
        loading={purge.isPending}
        onConfirm={async () => {
          await purge.mutateAsync();
          setPurgeOpen(false);
        }}
      />
    </div>
  );
}
